import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, Home, Calendar } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        const paymentRef = searchParams.get('ref');
        
        if (paymentRef) {
          const response = await axios.get(`${API}/payments/verify/${paymentRef}`, {
            withCredentials: true
          });
          setPaymentDetails(response.data);
        }
      } catch (err) {
        console.error('Failed to verify payment:', err);
        setError('Unable to verify payment status. Your payment may still be processing.');
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to allow ITN to process
    setTimeout(verifyPayment, 2000);
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-yellow-500 mx-auto mb-4" />
          <p className="text-white text-lg">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="text-center">
          <div className="mx-auto w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-12 w-12 text-green-500" />
          </div>
          <CardTitle className="text-2xl text-white">Payment Successful!</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-gray-400 text-center">
            Your booking has been confirmed. You will receive a confirmation email shortly.
          </p>

          {paymentDetails && (
            <div className="bg-gray-700/50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Payment Reference:</span>
                <span className="text-white font-mono">{paymentDetails.payment_reference}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Amount Paid:</span>
                <span className="text-green-400 font-semibold">R{paymentDetails.amount?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Status:</span>
                <span className={`font-semibold ${
                  paymentDetails.status === 'paid' ? 'text-green-400' : 'text-yellow-400'
                }`}>
                  {paymentDetails.status === 'paid' ? 'Confirmed' : 'Processing'}
                </span>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
              <p className="text-yellow-400 text-sm">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <Home className="h-4 w-4 mr-2" />
              Home
            </Button>
            <Button
              onClick={() => navigate('/my-bookings')}
              className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              <Calendar className="h-4 w-4 mr-2" />
              My Bookings
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
